import { type BaseRendererComponentProps, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    closeLimitBanner as closeLimit,
    PROMO_BANNER_ID,
    showIntervalBanner as showInterval,
    showLimitBanner as showLimit,
    storageKeyForBanner,
} from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoImproving';
import { BannerModel } from 'reactApp/modules/promoNew/models/bannerModel';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store as reduxStore } from 'reactApp/store';

const LazyComponent = React.lazy(() => import('reactApp/ui/AiPhotoImprovingPromo/AiPhotoImprovingBanner'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const aiPhotoImprovingBannerCreator: ModelCreator = ({ models, render, stores }) =>
    new BannerModel(
        PROMO_BANNER_ID,
        models.base,
        new BaseModelProps(PROMO_BANNER_ID, {
            store: new LocalStorageStore(storageKeyForBanner, stores.localStorage),
            renderer: new ReactRenderer(PROMO_BANNER_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit,
            closeLimit,
            showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            test: (resolve, reject) => {
                // Только десктоп
                if (IS_PHONE_BROWSER) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                // Показываем только в хомяке и в галерее
                if (!(storage === EStorageType.home || storage === EStorageType.gallery)) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = UserQuotaSelectors.isOverquota(state);
                const isCorp = UserSelectors.isCorpUser(state);

                // Не показываем бизам, корпам, платникам и оверквотерам
                if (isBizUser || isPaidUser || isCorp || isOverquota) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
